/**
 * Project type utilities for consistent icon and styling across the application
 */

/**
 * Project status constants for consistent usage across the application
 */
export const PROJECT_STATUS = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  IN_REVIEW: 'In Review',
  AGENCY_REVIEW: 'Agency Review',
  COMPLETED: 'Completed',
  DATES_NEEDED: 'Dates Needed',
  ARCHIVED: 'archived',
  CANCELED: 'Canceled',
  // Legacy statuses (backward compatibility)
  SCHEDULED: 'Scheduled',
  IN_PRODUCTION: 'In Production',
};

/**
 * Project status categories for consistent ordering across the application
 */
export const PROJECT_STATUSES = [
  PROJECT_STATUS.DRAFT,
  PROJECT_STATUS.ACTIVE,
  PROJECT_STATUS.AGENCY_REVIEW,
  PROJECT_STATUS.IN_REVIEW,
  PROJECT_STATUS.COMPLETED,
];

/**
 * Statuses a client should see under "Active".
 *
 * Agency Review belongs here: to the company it is the same thing as In Review —
 * submitted, out of their hands, waiting on someone. Client surfaces that
 * partition by an explicit status list drop anything not named, so a project in
 * Agency Review renders NOWHERE unless this list is the one they check.
 */
const ACTIVE_PROJECT_STATUSES = [
  PROJECT_STATUS.ACTIVE,
  PROJECT_STATUS.IN_REVIEW,
  PROJECT_STATUS.AGENCY_REVIEW,
];

export const isActiveProjectStatus = (status) =>
  ACTIVE_PROJECT_STATUSES.includes(status);

/** Submitted and waiting on a reviewer — no events, no dates to show yet. */
export const isAwaitingReviewStatus = (status) =>
  status === PROJECT_STATUS.IN_REVIEW || status === PROJECT_STATUS.AGENCY_REVIEW;

// Project types that skip the admin Review & Set Up wizard.
// - 'proposal' (MSA): the client already booked dates + deliverables via MsaOrderWizard.
// - 'episode' (podcast): episodes are set up through the podcast pipeline.
const REVIEW_WIZARD_EXCLUDED_TYPES = ['proposal', 'episode'];

/**
 * Is this project a candidate for the admin "Review & Set Up" wizard?
 * True only for a FRESH submission sitting In Review — not a client edit
 * (those carry pendingChanges/hasUnreviewedChanges and use the Acknowledge flow).
 */
export const isReviewSetupEligible = (project) =>
  !!project &&
  project.status === PROJECT_STATUS.IN_REVIEW &&
  !REVIEW_WIZARD_EXCLUDED_TYPES.includes(project.type) &&
  !project.hasUnreviewedChanges;

/**
 * Get the CSS class for styling project status tags
 * @param {string} status - The project status
 * @returns {string} Bulma color class
 */
export const getProjectStatusClass = (status) => {
  switch (status) {
    case PROJECT_STATUS.DRAFT:
      return "is-warning";
    case PROJECT_STATUS.ACTIVE:
      return "is-info";
    case PROJECT_STATUS.COMPLETED:
      return "is-success";
    // Legacy status support (for backward compatibility)
    case PROJECT_STATUS.SCHEDULED:
    case PROJECT_STATUS.IN_PRODUCTION:
      return "is-info";
    case PROJECT_STATUS.IN_REVIEW:
      return "is-link";
    case PROJECT_STATUS.AGENCY_REVIEW:
      return "is-warning";
    case "Post Production":
      return "is-link";
    case "Client Review":
      return "is-dark";
    case PROJECT_STATUS.DATES_NEEDED:
      return "is-danger";
    case PROJECT_STATUS.CANCELED:
      return "is-danger";
    case "Complete":
      return "is-success";
    default:
      return "is-light";
  }
};

/**
 * Display name for an episode: its public episodeTitle only once the client has
 * approved the title & show notes (`episodeMetaConfirmedAt`) — until then the
 * title is a draft, so the working project name stays put.
 */
export const episodeDisplayTitle = (project) =>
  (project?.episodeMetaConfirmedAt && project.episodeTitle?.trim()) || project?.name || '';

/**
 * Display title for a project. Episodes go through episodeDisplayTitle;
 * everything else shows the project name.
 */
export const projectDisplayName = (project) =>
  project?.type === 'episode' ? episodeDisplayTitle(project) : project?.name || '';

/**
 * Is this company (or project) managed by an agency?
 * An agency's OWN company record has agencyId null (and isAgency true), so this is
 * false for the agency itself — which is what keeps their own orders out of the
 * Agency Review gate and out of markup.
 */
const isAgencyLinked = (company) => !!company?.agencyId;

/**
 * Status a submitted project should land in.
 * Agency-linked companies get a review gate in front of ours; everyone else goes
 * straight to In Review exactly as before.
 *
 * Accepts anything carrying `agencyId` — a company doc at create time, or the
 * project itself on a later re-submit (projects are stamped with agencyId at
 * create, so no company lookup is needed for those).
 *
 * Podcast episodes never take the agency gate — it exists for MSA orders only.
 */
export const resolveSubmitStatus = (project) =>
  isAgencyLinked(project) && project.type !== 'episode'
    ? PROJECT_STATUS.AGENCY_REVIEW
    : PROJECT_STATUS.IN_REVIEW;
